import { useState, useEffect, useCallback } from 'react';
import Modal from 'react-bootstrap/Modal';
import { styled } from 'styled-components';
import { useForm } from 'react-hook-form';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { MdDelete, MdModeEdit, MdSave, MdCancel } from 'react-icons/md';
import moment from 'moment';

import { useAppContext } from '../../contextApi/context';
import { searchArticleInStock } from '../../services/StockService';
import { searchUsersByName } from '../../services/UsersService';
import { searchIssueTypesByName } from '../../services/IssueTypesService';
import { createMovementInWorkOrder } from '../../services/StockMovementService';
import { TableActionButton, ModalButton } from '../../styles/StyledComponents';
import CustomDateTimePicker from '../../components/shared/CustomDateTimePicker';
import { TWO_DECIMALS_GREATER_THAN_ZERO, ONLY_NUMBERS_GREATER_THAN_ZERO } from '../../constants/regex';

/**
 * Modal para crear un nuevo despacho
 * @param {boolean} show - Controla la visibilidad del modal
 * @param {Function} onHide - Función para cerrar el modal
 * @param {Function} onSuccess - Función a ejecutar cuando se guarda exitosamente
 * @param {number} workOrderId - ID de la orden de trabajo
 */
const CreateIssueMovementModal = ({ show, onHide, onSuccess, workOrderId }) => {
    // Context
    const { loggedUser } = useAppContext();

    // State
    const [timer, setTimer] = useState(null);
    const [details, setDetails] = useState([]);
    const [issueDate, setIssueDate] = useState(moment.utc(Date.now()).format());
    const [showDateTimeError, setShowDateTimeError] = useState(false);

    // Estados para manejo de artículos
    const [foundArticlesInStock, setFoundArticlesInStock] = useState([]);
    const [availableArticles, setAvailableArticles] = useState([]);
    const [selectedArticle, setSelectedArticle] = useState(null);
    const [showArticleError, setShowArticleError] = useState(null);
    const [showAmountError, setShowAmountError] = useState(false);
    const [articleNow, setArticleNow] = useState(Date.now());

    // Estados para manejo de usuarios
    const [availableUsers, setAvailableUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [showUserError, setShowUserError] = useState(false);

    // Estados para manejo de tipos de despacho
    const [availableIssueTypes, setAvailableIssueTypes] = useState([]);
    const [selectedIssueTypeId, setSelectedIssueTypeId] = useState(null);
    const [showIssueTypeError, setShowIssueTypeError] = useState(null);

    // Estados para validación de cantidad
    const [regexAmount, setRegexAmount] = useState(TWO_DECIMALS_GREATER_THAN_ZERO);

    // Estados para edición inline de detalles
    const [editingDetailIndex, setEditingDetailIndex] = useState(null);
    const [editingAmount, setEditingAmount] = useState('');
    const [editingShowAmountError, setEditingShowAmountError] = useState(false);
    const [editingInputError, setEditingInputError] = useState('');

    // Estados para manejo de errores y carga
    const [modalApiError, setModalApiError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Estado para checkbox de confirmación de datos
    const [isDataConfirmed, setIsDataConfirmed] = useState(false);

    // Forms
    const { register, handleSubmit, reset } = useForm({
        defaultValues: {},
    });

    const {
        register: register2,
        formState: { errors: errors2 },
        handleSubmit: handleSubmit2,
        reset: reset2,
    } = useForm({
        defaultValues: {
            amount: '',
        },
    });

    // Función para resetear el formulario
    const resetForm = useCallback(() => {
        setDetails([]);
        setIssueDate(moment.utc(Date.now()).format());
        setShowDateTimeError(false);
        setSelectedArticle(null);
        setShowArticleError(null);
        setShowAmountError(false);
        setSelectedUserId(null);
        setShowUserError(false);
        setSelectedIssueTypeId(null);
        setShowIssueTypeError(null);
        setModalApiError('');
        setIsSubmitting(false);
        setIsDataConfirmed(false); // Resetear checkbox de confirmación
        reset();
        reset2();
    }, [reset, reset2]);

    // Resetear formulario cuando se cierra el modal
    useEffect(() => {
        if (!show) {
            resetForm();
        }
    }, [show, resetForm]);

    /**
     * Maneja la validación de cantidad durante la escritura
     */
    const handleAmountChange = (e) => {
        setShowAmountError(false);
        if (selectedArticle && selectedArticle.amount < Number(e.target.value)) {
            setShowAmountError(true);
        }
    };

    /**
     * Maneja la adición de un nuevo detalle al despacho
     */
    const handleAddDetail = async (data) => {
        if (selectedArticle === null) {
            setShowArticleError(true);
        } else if (selectedArticle.amount < Number(data.amount)) {
            setShowAmountError(true);
        } else {
            const newDetail = {
                articleId: selectedArticle.articleId,
                articleName: selectedArticle.articleName,
                brand: selectedArticle.brand,
                amount: Number(data.amount),
                itemsValue: selectedArticle.averageItemsValue,
                measureUnit: selectedArticle.measureUnit,
                currentAmount: selectedArticle.amount,
            };
            setDetails([...details, newDetail]);
            clearAfterAddDetail();
            reset2();
        }
    };

    /**
     * Guarda el despacho usando el servicio createMovementInWorkOrder
     */
    const handleSaveDispatch = async (data) => {
        if (isAllDataFilled()) {
            const payload = {
                type: 'ISSUE',
                issueTypeId: selectedIssueTypeId,
                workOrderId: workOrderId,
                receivingUserId: selectedUserId,
                description: data.description,
                eventDate: issueDate,
                details: details.map((item) => ({
                    articleId: item.articleId,
                    amount: item.amount,
                    brand: item.brand,
                })),
            };

            setIsSubmitting(true);
            setModalApiError('');

            try {
                const response = await createMovementInWorkOrder(loggedUser.token, payload);

                if (response.status === 200 || response.status === 201) {
                    resetForm();
                    onHide();

                    if (onSuccess) {
                        onSuccess(response.data);
                    }
                } else {
                    setModalApiError('Error al crear el despacho. Por favor intente nuevamente.');
                }
            } catch (error) {
                setModalApiError('Error al crear el despacho. Por favor intente nuevamente.');
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    /**
     * Maneja errores de validación del formulario principal
     */
    const handleFormErrors = () => {
        isAllDataFilled();
    };

    /**
     * Maneja errores de validación del formulario de detalles
     */
    const handleForm2Errors = () => {
        if (selectedArticle === null) {
            setShowArticleError(true);
        }
    };

    /**
     * Maneja la selección de tipo de despacho
     */
    const handleSelectIssueType = async (event, newValue) => {
        if (newValue != null) {
            setShowIssueTypeError(false);
            setSelectedIssueTypeId(newValue.id);
        } else {
            setSelectedIssueTypeId('');
        }
    };

    /**
     * Busca tipos de despacho por nombre con debounce
     * Solo muestra tipos que requieren vehículo (vehicleRequired: true)
     */
    const handleSearchIssueTypes = (e, newValue, reason) => {
        clearTimeout(timer);

        const newTimer = setTimeout(async () => {
            if (reason === 'clear') {
                setAvailableIssueTypes([]);
                setSelectedIssueTypeId(null);
            }

            if (newValue !== '') {
                if (reason === 'input') {
                    const response = await searchIssueTypesByName(loggedUser.token, newValue);

                    if (response && Array.isArray(response) && response.length > 0) {
                        // Filtrar solo tipos de despacho que requieren vehículo
                        const filteredIssueTypes = response.filter((issueType) => issueType.vehicleRequired === true);

                        setAvailableIssueTypes(
                            filteredIssueTypes.map((issueType) => ({
                                id: issueType.id,
                                label: issueType.name,
                                vehicleRequired: issueType.vehicleRequired,
                            }))
                        );
                    } else {
                        setAvailableIssueTypes([]);
                    }
                }
            }
        }, 500);

        setTimer(newTimer);
    };

    /**
     * Maneja la selección de usuario solicitante
     */
    const handleSelectUser = async (event, newValue) => {
        if (newValue != null) {
            setShowUserError(false);
            setSelectedUserId(newValue.id);
        } else {
            setSelectedUserId(null);
            setAvailableUsers([]);
        }
    };

    /**
     * Busca usuarios por nombre con debounce
     */
    const handleSearchUsers = (e, newValue, reason) => {
        clearTimeout(timer);

        const newTimer = setTimeout(async () => {
            if (reason === 'clear') {
                setAvailableUsers([]);
                setSelectedUserId(null);
            }

            if (newValue !== '') {
                if (reason === 'input') {
                    const response = await searchUsersByName(loggedUser.token, newValue);

                    if (response && Array.isArray(response) && response.length > 0) {
                        setAvailableUsers(
                            response.map((user) => ({
                                id: user.id,
                                label: `${user.firstName} ${user.lastName}`,
                            }))
                        );
                    } else {
                        setAvailableUsers([]);
                    }
                }
            }
        }, 500);

        setTimer(newTimer);
    };

    /**
     * Maneja la selección de artículo y ajusta la validación según la unidad de medida
     */
    const handleSelectArticle = (event, newValue) => {
        if (newValue != null) {
            setShowArticleError(false);
            const found = foundArticlesInStock.find((article) => article.id === newValue.id);

            setSelectedArticle(found);

            // Ajustar regex de validación según unidad de medida
            if (found.measureUnit === 'UNITS') {
                setRegexAmount(ONLY_NUMBERS_GREATER_THAN_ZERO);
            } else {
                setRegexAmount(TWO_DECIMALS_GREATER_THAN_ZERO);
            }
        } else {
            setSelectedArticle(null);
        }
    };

    /**
     * Busca artículos en stock por nombre con debounce
     */
    const handleSearchArticle = (e, newValue, reason) => {
        clearTimeout(timer);

        const newTimer = setTimeout(async () => {
            if (reason === 'clear') {
                reset2({ amount: '' });
                setSelectedArticle(null);
                setFoundArticlesInStock([]);
                setAvailableArticles([]);
                setArticleNow(Date.now());
            }

            if (newValue !== '') {
                if (reason === 'input') {
                    let response = await searchArticleInStock(loggedUser.token, newValue);

                    if (response && Array.isArray(response) && response.length > 0) {
                        // Mapear con ID incremental como en la página
                        response = response.map((item, index) => {
                            return {
                                ...item,
                                id: index,
                            };
                        });

                        // Filtrar artículos ya seleccionados como en la página
                        const articleIdsSelected = details.map((detail) => detail.articleId);
                        const articleBrandsSelected = details.map((detail) => detail.brand);

                        response = response.filter((article) => {
                            return !(articleIdsSelected.includes(article.articleId) && articleBrandsSelected.includes(article.brand));
                        });

                        setFoundArticlesInStock(response);
                        setAvailableArticles(
                            response?.map((article) => ({
                                id: article.id,
                                label: `${article.internalCode} - ${article.articleName} (${article.brand})`,
                            }))
                        );
                    } else {
                        setFoundArticlesInStock([]);
                        setAvailableArticles([]);
                    }
                }
            } else {
                setAvailableArticles([]);
                setSelectedArticle(null);
            }
        }, 500);

        setTimer(newTimer);
    };

    /**
     * Limpia los campos relacionados con artículos después de agregar un detalle
     */
    const clearAfterAddDetail = () => {
        setSelectedArticle(null);
        setShowArticleError(null);
        setShowAmountError(false);
        setAvailableArticles([]);
        setFoundArticlesInStock([]);
        setArticleNow(Date.now());
    };

    /**
     * Abre la edición inline para un detalle específico
     */
    const handleOpenDetail = (index) => {
        const detail = details[index];
        setEditingDetailIndex(index);
        setEditingAmount(String(detail.amount));
        setEditingShowAmountError(false);
        setEditingInputError('');
    };

    /**
     * Guarda la edición inline de cantidad en un detalle
     */
    const handleSaveInlineEdit = (index) => {
        const detail = details[index];

        // Validación de formato según unidad de medida
        const pattern = detail.measureUnit === 'UNITS' ? ONLY_NUMBERS_GREATER_THAN_ZERO : TWO_DECIMALS_GREATER_THAN_ZERO;

        if (!pattern.test(editingAmount)) {
            setEditingInputError(
                detail.measureUnit === 'UNITS' ? 'Sólo se permiten números enteros (5 - 100)' : 'Sólo se permiten números (5 - 3.50)'
            );
            return;
        }

        const numeric = Number(editingAmount);
        if (detail.currentAmount < numeric) {
            setEditingShowAmountError(true);
            return;
        }

        // Actualizar la cantidad del detalle
        const updatedDetails = [...details];
        updatedDetails[index] = { ...updatedDetails[index], amount: numeric };
        setDetails(updatedDetails);

        // Resetear estado de edición inline
        setEditingDetailIndex(null);
        setEditingAmount('');
        setEditingShowAmountError(false);
        setEditingInputError('');
    };

    /**
     * Cancela la edición inline
     */
    const handleCancelInlineEdit = () => {
        setEditingDetailIndex(null);
        setEditingAmount('');
        setEditingShowAmountError(false);
        setEditingInputError('');
    };

    /**
     * Maneja cambios en el campo de cantidad durante edición inline
     */
    const handleEditingAmountChange = (e) => {
        setEditingAmount(e.target.value);
        setEditingShowAmountError(false);
        setEditingInputError('');
    };

    /**
     * Elimina un detalle de la lista
     */
    const handleDelete = (index) => {
        const updatedDetails = details.filter((_, i) => i !== index);
        setDetails(updatedDetails);
    };

    /**
     * Valida que todos los campos requeridos estén completos
     */
    const isAllDataFilled = () => {
        let isOk = true;

        if (!issueDate) {
            setShowDateTimeError(true);
            isOk = false;
        }

        if (selectedIssueTypeId === null) {
            setShowIssueTypeError(true);
            isOk = false;
        }

        if (selectedUserId === null) {
            setShowUserError(true);
            isOk = false;
        }

        return isOk;
    };

    /**
     * Maneja cambios en la fecha y hora del despacho
     */
    const handleDateChange = (date) => {
        setIssueDate(date);
        setShowDateTimeError(false);
    };

    // Estilos para los iconos del Autocomplete
    const autocompleteIconStyles = {
        '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
            outline: 'none',
            backgroundColor: 'transparent',
            '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
            '&:focus': {
                outline: 'none',
                backgroundColor: 'transparent',
            },
            '&:focus-visible': {
                outline: 'none',
                backgroundColor: 'transparent',
            },
        },
        '& .MuiInputLabel-root': {
            fontWeight: 'normal',
        },
        '& .MuiInputBase-root': {
            height: '40px',
        },
    };

    return (
        <>
            <StyledModal show={show} onHide={onHide} size='lg' backdrop='static' centered>
                <Modal.Header className='fw-bold' closeButton>
                    Crear Despacho
                </Modal.Header>

                <Modal.Body>
                    {modalApiError && (
                        <ApiErrorAlert className='alert alert-danger text-center mb-3' role='alert'>
                            {modalApiError}
                        </ApiErrorAlert>
                    )}

                    {/* Sección: Información General */}
                    <Section>
                        <SectionTitle>Información General</SectionTitle>

                        <form id='dispatch-form' onSubmit={handleSubmit(handleSaveDispatch, handleFormErrors)} style={{ display: 'grid' }}>
                            <FormGrid columns={2}>
                                {/* Fecha y hora */}
                                <div>
                                    <label className='mb-1'>Fecha y hora *</label>
                                    <CustomDateTimePicker defaultDate={moment()} minDate={null} maxDate={moment()} actionHandler={handleDateChange} />
                                    {showDateTimeError && <ErrorText>Debe seleccionar una fecha y hora</ErrorText>}
                                </div>

                                {/* Tipo de despacho */}
                                <div>
                                    <label className='mb-1'>Tipo de despacho *</label>
                                    <Autocomplete
                                        key={`issue-type-${selectedIssueTypeId}`}
                                        disablePortal
                                        size='small'
                                        options={availableIssueTypes}
                                        value={
                                            selectedIssueTypeId && availableIssueTypes.length > 0
                                                ? availableIssueTypes.find((issueType) => issueType.id === selectedIssueTypeId) || null
                                                : null
                                        }
                                        renderInput={(params) => <TextField {...params} label='Buscar por nombre' error={showIssueTypeError} />}
                                        onChange={handleSelectIssueType}
                                        onInputChange={handleSearchIssueTypes}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        noOptionsText='Buscar por nombre'
                                        disabled={isSubmitting}
                                        sx={autocompleteIconStyles}
                                    />
                                    {showIssueTypeError && <ErrorText>Debe seleccionar un tipo de despacho</ErrorText>}
                                </div>

                                {/* Usuario solicitante */}
                                <div>
                                    <label className='mb-1'>Usuario solicitante *</label>
                                    <Autocomplete
                                        key={`user-${selectedUserId}`}
                                        disablePortal
                                        size='small'
                                        options={availableUsers}
                                        value={
                                            selectedUserId && availableUsers.length > 0
                                                ? availableUsers.find((user) => user.id === selectedUserId) || null
                                                : null
                                        }
                                        renderInput={(params) => <TextField {...params} label='Buscar por nombre o apellido' error={showUserError} />}
                                        onChange={handleSelectUser}
                                        onInputChange={handleSearchUsers}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        noOptionsText='Buscar por nombre o apellido'
                                        disabled={isSubmitting}
                                        sx={autocompleteIconStyles}
                                    />
                                    {showUserError && <ErrorText>Debe seleccionar un usuario solicitante</ErrorText>}
                                </div>

                                {/* Descripción */}
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <label className='mb-1'>Descripción</label>
                                    <textarea
                                        className='form-control form-control-sm'
                                        placeholder='Descripción opcional...'
                                        rows='3'
                                        maxLength='200'
                                        {...register('description')}
                                        disabled={isSubmitting}
                                    />
                                </div>
                            </FormGrid>
                        </form>
                    </Section>

                    <Divider />

                    {/* Sección: Detalles del Despacho */}
                    <Section>
                        <SectionTitle>Detalles del Despacho</SectionTitle>

                        <FormContainer>
                            <FormStyled onSubmit={handleSubmit2(handleAddDetail, handleForm2Errors)}>
                                <FormGrid columns={2}>
                                    {/* Artículo */}
                                    <div>
                                        <label className='mb-1'>Artículo *</label>
                                        <Autocomplete
                                            key={`article-${articleNow}`}
                                            disablePortal
                                            size='small'
                                            options={availableArticles}
                                            value={
                                                selectedArticle && availableArticles.length > 0
                                                    ? availableArticles.find((article) => article.id === selectedArticle.id) || null
                                                    : null
                                            }
                                            renderInput={(params) => (
                                                <TextField {...params} label='Buscar por nombre o código' error={showArticleError} />
                                            )}
                                            onChange={handleSelectArticle}
                                            onInputChange={handleSearchArticle}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                            noOptionsText='Buscar por nombre o código'
                                            disabled={isSubmitting}
                                            sx={autocompleteIconStyles}
                                        />
                                        <InfoText className={`${selectedArticle ? 'info' : ''}`}>
                                            {showArticleError && 'Campo requerido'}
                                            {selectedArticle && `Cantidad actual: ${selectedArticle.amount}`}
                                        </InfoText>
                                    </div>

                                    {/* Cantidad */}
                                    <div>
                                        <label className='mb-1'>Cantidad *</label>
                                        <input
                                            type='number'
                                            className={`form-control form-control-sm ${errors2.amount || showAmountError ? 'is-invalid' : ''}`}
                                            placeholder='0'
                                            step='any'
                                            {...register2('amount', {
                                                required: true,
                                                pattern: regexAmount,
                                                onChange: handleAmountChange, // AGREGADO: Para validación en tiempo real como en la página
                                            })}
                                            disabled={isSubmitting || !selectedArticle}
                                        />
                                        {errors2.amount?.type === 'required' && <ErrorText>Este campo es requerido</ErrorText>}
                                        {errors2.amount?.type === 'pattern' && selectedArticle?.measureUnit !== 'UNITS' && (
                                            <ErrorText>Sólo se permiten números (5 - 3.50)</ErrorText>
                                        )}
                                        {errors2.amount?.type === 'pattern' && selectedArticle?.measureUnit === 'UNITS' && (
                                            <ErrorText>Sólo se permiten números enteros (5 - 100)</ErrorText>
                                        )}
                                        {showAmountError && <ErrorText>Debe ser menor o igual a la cantidad actual</ErrorText>}
                                    </div>
                                </FormGrid>

                                <ButtonContainer>
                                    <button className='btn btn-danger' type='submit' disabled={isSubmitting}>
                                        + Agregar detalle
                                    </button>
                                </ButtonContainer>
                            </FormStyled>
                        </FormContainer>

                        {/* Tabla de detalles */}
                        {details.length > 0 && (
                            <TableContainer>
                                <table className='table table-bordered'>
                                    <thead>
                                        <tr>
                                            <th scope='col'>Artículo</th>
                                            <th scope='col'>Marca</th>
                                            <th scope='col'>Cantidad</th>
                                            <th scope='col'>Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {details.map((detail, index) => (
                                            <tr key={index}>
                                                <td>{detail.articleName}</td>
                                                <td>{detail.brand}</td>
                                                <td>
                                                    {editingDetailIndex === index ? (
                                                        <EditingCellContainer hasError={editingShowAmountError || editingInputError}>
                                                            <input
                                                                className={`form-control form-control-sm ${
                                                                    editingShowAmountError || editingInputError ? 'is-invalid' : ''
                                                                }`}
                                                                type='text'
                                                                value={editingAmount}
                                                                maxLength={10}
                                                                onChange={handleEditingAmountChange}
                                                            />
                                                            <div className='error-container'>
                                                                {editingInputError && <ErrorText>{editingInputError}</ErrorText>}
                                                                {editingShowAmountError && (
                                                                    <ErrorText>Debe ser menor o igual a la cantidad actual</ErrorText>
                                                                )}
                                                            </div>
                                                        </EditingCellContainer>
                                                    ) : (
                                                        detail.amount
                                                    )}
                                                </td>
                                                <td>
                                                    <div className='d-flex'>
                                                        {editingDetailIndex === index ? (
                                                            <>
                                                                <TableActionButton
                                                                    className='btn btn-primary'
                                                                    onClick={() => handleSaveInlineEdit(index)}
                                                                >
                                                                    <MdSave />
                                                                    Guardar
                                                                </TableActionButton>
                                                                <TableActionButton className='btn btn-secondary' onClick={handleCancelInlineEdit}>
                                                                    <MdCancel />
                                                                    Cancelar
                                                                </TableActionButton>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <TableActionButton
                                                                    className='btn btn-primary'
                                                                    onClick={() => handleOpenDetail(index)}
                                                                >
                                                                    <MdModeEdit />
                                                                    Editar
                                                                </TableActionButton>
                                                                <TableActionButton className='btn btn-danger' onClick={() => handleDelete(index)}>
                                                                    <MdDelete />
                                                                    Eliminar
                                                                </TableActionButton>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </TableContainer>
                        )}
                    </Section>

                    {/* Checkbox de confirmación de datos */}
                    <Section>
                        <ConfirmationCheckboxContainer>
                            <label className='checkbox-wrapper'>
                                <input
                                    type='checkbox'
                                    id='confirmData'
                                    checked={isDataConfirmed}
                                    onChange={(e) => setIsDataConfirmed(e.target.checked)}
                                    disabled={isSubmitting}
                                />
                                <span className='checkmark'></span>
                                <span className='checkbox-label'>Confirmo que todos los datos digitados son correctos</span>
                            </label>
                            <span className='checkbox-hint'>Verifique que toda la información del despacho sea correcta antes de crear</span>
                        </ConfirmationCheckboxContainer>
                    </Section>
                </Modal.Body>

                <Modal.Footer>
                    <ModalButton className='cancel' onClick={onHide} disabled={isSubmitting}>
                        Cancelar
                    </ModalButton>
                    <ModalButton
                        className='action'
                        type='submit'
                        form='dispatch-form'
                        disabled={isSubmitting || details.length === 0 || !isDataConfirmed}
                    >
                        {isSubmitting ? (
                            <>
                                <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
                                Guardando...
                            </>
                        ) : (
                            'Crear despacho'
                        )}
                    </ModalButton>
                </Modal.Footer>
            </StyledModal>
        </>
    );
};

// Styled Components
const StyledModal = styled(Modal)`
    .modal-body {
        max-height: 70vh;
        overflow-y: auto;
    }
`;

const Section = styled.div`
    margin-bottom: 20px;
`;

const SectionTitle = styled.h6`
    font-size: 15px;
    font-weight: 600;
    color: #212529;
    margin-bottom: 15px;
    margin-top: 0;
`;

const FormContainer = styled.div`
    max-width: 800px;
    margin: 0 auto;
`;

const FormStyled = styled.form`
    display: grid;
`;

const FormGrid = styled.div`
    display: grid;
    grid-template-columns: ${(props) => (props.columns === 2 ? '1fr 1fr' : '1fr')};
    gap: 15px;
    margin-bottom: 15px;

    > div {
        display: flex;
        flex-direction: column;

        label {
            font-size: 14px;
            font-weight: 600;
            color: #495057;
        }

        input,
        textarea,
        .MuiAutocomplete-root {
            width: 100%;
        }

        input[type='number'] {
            height: 40px;
            padding: 8.5px 14px;
            font-size: 16px;
        }

        .MuiTextField-root {
            width: 100%;

            .MuiInputBase-root {
                height: 40px;
            }
        }

        textarea {
            resize: vertical;
            min-height: 80px;
        }
    }

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 10px;

    button {
        background-color: #d04437;
        min-width: 180px;
    }
`;

const Divider = styled.hr`
    margin: 15px 0;
    border-top: 2px solid #dee2e6;
`;

const TableContainer = styled.div`
    margin-top: 20px;

    table {
        margin-bottom: 0;
        table-layout: fixed;
        width: 100%;
    }

    th {
        background-color: #f8f9fa;
        font-size: 13px;
        font-weight: 600;
    }

    td {
        vertical-align: middle;
        font-size: 13px;
        height: 45px; /* Altura fija para todas las filas */
        position: relative;
    }

    /* Ancho fijo para columna de cantidad */
    th:nth-child(3),
    td:nth-child(3) {
        width: 100px;
    }

    /* Ancho fijo para columna de acciones */
    th:nth-child(4),
    td:nth-child(4) {
        width: 180px;
    }
`;

const EditingCellContainer = styled.div`
    position: relative;
    height: 100%;
    display: flex;
    align-items: center;

    input {
        width: 80px;
        height: 28px;
        font-size: 13px;
        border: none;
        background: transparent;
        padding: 0;
        outline: none;

        &:focus {
            background: #f8f9fa;
            border: 1px solid #007bff;
            border-radius: 3px;
            padding: 2px 4px;
        }

        &.is-invalid {
            background: #fff5f5;
            border: 1px solid #dc3545;
            border-radius: 3px;
            padding: 2px 4px;
        }
    }

    /* Contenedor de errores posicionado absolutamente para no afectar altura */
    .error-container {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        z-index: 10;
        background: white;
        border: 1px solid #ddd;
        border-top: none;
        border-radius: 0 0 4px 4px;
        padding: 2px 4px;
        font-size: 11px;

        /* Solo mostrar si hay errores */
        display: ${(props) => (props.hasError ? 'block' : 'none')};
    }
`;

const ErrorText = styled.span`
    color: #d04437;
    font-size: 12px;
    margin-top: 4px;
`;

const InfoText = styled.span`
    color: #d04437;
    font-size: 12px;
    margin-top: 4px;
    min-height: 12px;

    &.info {
        color: green;
        font-weight: 500;
    }
`;

const ConfirmationCheckboxContainer = styled.div`
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 15px;
    margin-top: 0;
    margin-bottom: 0;

    .checkbox-wrapper {
        display: flex;
        align-items: flex-start;
        cursor: pointer;
        position: relative;
        user-select: none;
        gap: 12px;

        input[type='checkbox'] {
            position: absolute;
            opacity: 0;
            cursor: pointer;
            height: 0;
            width: 0;

            &:checked ~ .checkmark {
                background-color: #d04437;
                border-color: #d04437;

                &:after {
                    display: block;
                }
            }

            &:focus ~ .checkmark {
                box-shadow: 0 0 0 0.2rem rgba(208, 68, 55, 0.25);
            }

            &:disabled ~ .checkmark {
                background-color: #e9ecef;
                border-color: #ced4da;
                cursor: not-allowed;
            }

            &:disabled ~ .checkbox-label {
                color: #6c757d;
                cursor: not-allowed;
            }
        }

        .checkmark {
            position: relative;
            height: 20px;
            width: 20px;
            min-width: 20px;
            background-color: #fff;
            border: 2px solid #ced4da;
            border-radius: 4px;
            transition: all 0.2s ease;
            margin-top: 2px;

            &:after {
                content: '';
                position: absolute;
                display: none;
                left: 5px;
                top: 1px;
                width: 6px;
                height: 11px;
                border: solid white;
                border-width: 0 2px 2px 0;
                transform: rotate(45deg);
            }
        }

        .checkbox-label {
            font-size: 15px;
            font-weight: 500;
            color: #495057;
            line-height: 1.4;
        }

        &:hover .checkmark {
            border-color: #d04437;
        }
    }

    .checkbox-hint {
        display: block;
        font-size: 13px;
        color: #6c757d;
        margin-top: 8px;
        margin-left: 32px;
        line-height: 1.4;
    }
`;

const ApiErrorAlert = styled.div``;

export default CreateIssueMovementModal;
